import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col } from 'react-bootstrap';
import { getButtonStyle } from 'utils/formStyles';

const GuideFlip = ({ show, onClose, guideType }) => {
  // Responsive width state
  const [panelWidth, setPanelWidth] = useState('50vw');
  
  // Handle responsive width
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth <= 768 ? '100vw' : '50vw';
      setPanelWidth(newWidth);
    };
    
    // Set initial width
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getGuideContent = () => {
    switch (guideType) {
      case 'keyword':
        return {
          title: "Keyword-Based Writer Guide",
          phases: [
            {
              title: "Phase 1: Input Preparation",
              icon: "📝",
              time: "2-3 minutes",
              steps: [
                {
                  number: 1,
                  title: "Enter Your Title",
                  description: "Create a compelling, keyword-rich title that clearly indicates what your content is about.",
                  example: "Best Project Management Tools for Remote Teams in 2024"
                },
                {
                  number: 2,
                  title: "Add Primary Keyword",
                  description: "Enter your main target keyword from your keyword research. This will guide the AI in structuring your content.",
                  example: "project management tools remote"
                },
                {
                  number: 3,
                  title: "Include Brand Name",
                  description: "Add your company name for brand mentions and to maintain consistent voice.",
                  example: "TechStartup Inc."
                },
                {
                  number: 4,
                  title: "Select Writing Style",
                  description: "Choose the tone that matches your audience and brand voice.",
                  example: "Selected: Professional & Informative"
                },
                {
                  number: 5,
                  title: "Set Word Count",
                  description: "Specify target length (max 1,200 words for optimal readability).",
                  example: "800 words (optimal for this topic)"
                }
              ]
            },
            {
              title: "Phase 2: AI Processing",
              icon: "⚡",
              time: "10-15 seconds",
              steps: [
                {
                  number: 6,
                  title: "Click Generate Outline",
                  description: "The AI analyzes your inputs and creates a structured outline based on SEO best practices and user intent."
                },
                {
                  number: 7,
                  title: "AI Analysis in Progress",
                  description: "System performs: keyword analysis, competitor research, search intent matching, and content structure optimization."
                }
              ]
            },
            {
              title: "Phase 3: Outline Generation",
              icon: "✅",
              time: "Instant",
              steps: [
                {
                  number: 8,
                  title: "Receive Structured Outline",
                  description: "Get a properly organized content outline with clear sections, subheadings, and key points."
                }
              ]
            }
          ]
        };
      case 'content':
        return {
          title: "Content-Based Writer Guide",
          phases: [
            {
              title: "Phase 1: Content Preparation",
              icon: "📋",
              time: "2-3 minutes",
              steps: [
                {
                  number: 1,
                  title: "Paste Your Existing Content",
                  description: "Copy and paste your blog post, draft article, or any text content that needs restructuring. Can be anywhere from 10 to 5,000 words."
                },
                {
                  number: 2,
                  title: "Add Target Keyword",
                  description: "Enter the primary keyword you want to optimize for. This helps the AI understand your SEO goals and adjust the content structure accordingly."
                },
                {
                  number: 3,
                  title: "Select Writing Style",
                  description: "Choose the tone that best matches your brand and audience. The AI will maintain this voice while restructuring your content."
                }
              ]
            },
            {
              title: "Phase 2: AI Content Analysis",
              icon: "⚙️",
              time: "30-45 seconds",
              steps: [
                {
                  number: 4,
                  title: "Content Analysis",
                  description: "AI analyzes your content to identify main topics, supporting points, and areas that need improvement or expansion."
                },
                {
                  number: 5,
                  title: "Structure Optimization",
                  description: "System reorganizes content into logical sections with proper heading hierarchy (H1, H2, H3) for better readability and SEO."
                },
                {
                  number: 6,
                  title: "Keyword Integration",
                  description: "AI strategically places your primary keyword and identifies LSI keywords already in your content for optimal density."
                }
              ]
            },
            {
              title: "Phase 3: Optimized Output",
              icon: "✅",
              time: "Immediate",
              steps: [
                {
                  number: 7,
                  title: "Receive Structured Outline",
                  description: "Get a properly organized content outline with clear sections, subheadings, and key points extracted from your original content."
                },
                {
                  number: 8,
                  title: "Get Optimized Draft",
                  description: "Receive a fully rewritten draft that maintains your original ideas but with improved structure, flow, and SEO optimization."
                }
              ]
            }
          ]
        };
      case 'url':
        return {
          title: "URL-Based Writer Guide",
          phases: [
            {
              title: "Phase 1: Content Analysis",
              icon: "📊",
              time: "1-2 minutes",
              steps: [
                {
                  number: 1,
                  title: "Enter Your Page URL",
                  description: "Paste the URL of your existing content that needs optimization. The system will crawl and analyze your current page."
                },
                {
                  number: 2,
                  title: "System Crawls Your Page",
                  description: "AI analyzes your current content structure, keyword usage, readability, and identifies gaps compared to top-ranking pages."
                },
                {
                  number: 3,
                  title: "Competitor Comparison",
                  description: "Automatically compares your content against top 10 ranking pages for your target keyword."
                }
              ]
            },
            {
              title: "Phase 2: AI Optimization",
              icon: "⚡",
              time: "5-7 minutes",
              steps: [
                {
                  number: 4,
                  title: "AI Processing Begins",
                  description: "System generates optimized outline based on gap analysis, competitor insights, and SEO best practices."
                },
                {
                  number: 5,
                  title: "Content Gap Filling",
                  description: "AI identifies missing topics and sections that top-ranking pages include but yours doesn't."
                },
                {
                  number: 6,
                  title: "Structure Optimization",
                  description: "Reorganizes content flow for better readability and user engagement based on successful patterns."
                }
              ]
            },
            {
              title: "Phase 3: Content Delivery",
              icon: "✅",
              time: "Immediate",
              steps: [
                {
                  number: 7,
                  title: "Receive Optimized Outline",
                  description: "Get a complete content outline with all missing sections, improved structure, and keyword placement guidance."
                },
                {
                  number: 8,
                  title: "Content Draft Generation",
                  description: "System provides a content draft with new sections pre-written, ready for you to review and merge with existing content."
                },
                {
                  number: 9,
                  title: "Implementation Guide",
                  description: "Receive step-by-step instructions on how to update your existing content with the new optimized version."
                }
              ]
            }
          ]
        };
      default:
        return {
          title: "Writer Guide",
          phases: [
            {
              title: "Get Started",
              icon: "🚀",
              time: "5 minutes",
              steps: [
                {
                  number: 1,
                  title: "Follow the Steps",
                  description: "Complete each phase to create optimized content.",
                  example: "Read each step carefully for best results"
                }
              ]
            }
          ]
        };
    }
  };

  const guideContent = getGuideContent();

  return (
    <>
      {/* Overlay */}
      {show && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
          style={{ zIndex: 1040 }}
          onClick={onClose}
        />
      )}
      
      {/* Flip Panel */}
      <div 
        className={`position-fixed end-0 bg-white shadow-lg border-start border-2 border-primary`}
        style={{ 
          top: '50px', // Add gap from header
          width: panelWidth,
          height: 'calc(100vh - 50px)', // Adjust height to account for header
          zIndex: 1050,
          transform: show ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out',
          overflowY: 'auto'
        }}
      >
        <div className="p-4 h-100 d-flex flex-column">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3" style={{ minHeight: '60px' }}>
            <div className="flex-grow-1 me-3">
              <h4 className="fw-bold text-primary mb-0" style={{ lineHeight: '1.2', fontSize: '1.25rem' }}>
                {guideContent.title}
              </h4>
              <small className="text-muted">Step-by-step guide to get started</small>
            </div>
          </div>
          

          {/* Journey Phases */}
          <div className="flex-grow-1 overflow-auto mb-4">
            {guideContent.phases.map((phase, phaseIndex) => (
              <Card key={phaseIndex} className="mb-4 border-0 shadow-sm">
                <Card.Body className="p-4">
                  {/* Phase Header */}
                  <div className="d-flex align-items-center mb-3">
                    <div 
                      className="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                      style={{ 
                        width: '48px', 
                        height: '48px', 
                        fontSize: '24px',
                        background: phaseIndex === 0 ? 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)' :
                                   phaseIndex === 1 ? 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)' :
                                   'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
                      }}
                    >
                      {phase.icon}
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="fw-bold text-dark mb-1" style={{ fontSize: '18px' }}>
                        {phase.title}
                      </h5>
                    </div>
                    <div 
                      className="px-3 py-1 rounded-pill"
                      style={{ 
                        backgroundColor: '#f1f5f9',
                        fontSize: '12px',
                        color: '#64748b',
                        fontWeight: '600'
                      }}
                    >
                      {phase.time}
                    </div>
                  </div>

                  {/* Phase Steps */}
                  <div className="ms-2">
                    {phase.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="mb-3 p-3 rounded-3" style={{ backgroundColor: '#f8fafc' }}>
                        <div className="d-flex align-items-start">
                          <div 
                            className="rounded-circle bg-white border border-2 border-light d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                            style={{ 
                              width: '32px', 
                              height: '32px', 
                              fontSize: '14px',
                              fontWeight: '700',
                              color: '#64748b'
                            }}
                          >
                            {step.number}
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="fw-bold text-dark mb-2" style={{ fontSize: '14px' }}>
                              {step.title}
                            </h6>
                            <p className="text-muted small mb-2" style={{ fontSize: '13px', lineHeight: '1.5' }}>
                              {step.description}
                            </p>
                            {step.example && (
                              <div 
                                className="p-2 rounded"
                                style={{ 
                                  backgroundColor: 'white',
                                  borderLeft: '3px solid #3b82f6',
                                  fontSize: '12px',
                                  color: '#1e40af',
                                  fontFamily: 'monospace'
                                }}
                              >
                                {step.example}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>

          {/* Pro Tips */}
          <Card className="border-0" style={{ backgroundColor: '#fef3c7' }}>
            <Card.Body className="p-3">
              <h6 className="fw-bold mb-3" style={{ color: '#92400e', fontSize: '16px' }}>
                💡 Pro Tips for Success
              </h6>
              <div className="row">
                <div className="col-12">
                  <div className="d-flex align-items-start mb-2">
                    <span className="me-2" style={{ color: '#78350f', fontSize: '12px' }}>💡</span>
                    <span style={{ color: '#78350f', fontSize: '14px' }}>
                      Keep paragraphs short (2-3 sentences) for better readability on all devices
                    </span>
                  </div>
                  <div className="d-flex align-items-start mb-2">
                    <span className="me-2" style={{ color: '#78350f', fontSize: '12px' }}>💡</span>
                    <span style={{ color: '#78350f', fontSize: '14px' }}>
                      Use your primary keyword in the first 100 words of your content
                    </span>
                  </div>
                  <div className="d-flex align-items-start mb-2">
                    <span className="me-2" style={{ color: '#78350f', fontSize: '12px' }}>💡</span>
                    <span style={{ color: '#78350f', fontSize: '14px' }}>
                      Include LSI keywords naturally throughout the content (3-5 variations)
                    </span>
                  </div>
                  <div className="d-flex align-items-start mb-0">
                    <span className="me-2" style={{ color: '#78350f', fontSize: '12px' }}>💡</span>
                    <span style={{ color: '#78350f', fontSize: '14px' }}>
                      Add at least one internal link and 2-3 authoritative external links
                    </span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Footer */}
          <div className="mt-4 pt-3 border-top">
            <div className="text-center">
              <small className="text-muted">
                Need more help? Contact our support team for assistance.
              </small>
            </div>
          </div>

          {/* Close button below footer */}
          <div className="mt-3">
            <button
              onClick={onClose}
              className="btn btn-secondary w-100"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GuideFlip;